import React, { useState, useEffect, useCallback, useRef } from 'react';
import { S } from '../../styles';
import { API, authHeaders } from '../../utils/helpers';

// ── Settings › Website › Sign-ups (3 Nov) ────────────────────────────────────
//
// Who signed up on the website:
//   Email   the "Stay up to date" box (and lists brought in from an old
//           Mailchimp or Workstand export). Download as a spreadsheet to send
//           a newsletter from any email service; remove an address on request.
//   Text    the Text Updates form, with whether they agreed to texts.

async function api(path, opts = {}) {
  const res = await fetch(`${API}/api/website${path}`, {
    credentials: 'include', ...opts,
    headers: { ...authHeaders(false), ...(opts.headers || {}) },
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || d.message || `Spinboard answered ${res.status}.`);
  return d;
}

const day = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const SOURCE = { website: 'Website', import: 'Imported', mailchimp: 'Mailchimp', workstand: 'Workstand' };

export default function WebsiteSignups({ T, isDark, btn, sub }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [q, setQ] = useState('');
  const [source, setSource] = useState('mailchimp');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { setData(await api('/admin/signups')); setError(''); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setBusy('download');
    try {
      const res = await fetch(`${API}/api/website/admin/signups/emails.csv`, { credentials: 'include', headers: authHeaders(false) });
      if (!res.ok) throw new Error(`Spinboard answered ${res.status}.`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a'); a.href = url; a.download = 'email-signups.csv'; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { setError(e.message); }
    setBusy('');
  };
  const importFile = async (file) => {
    if (!file) return;
    setBusy('import'); setNote(''); setError('');
    try {
      const d = await api(`/admin/signups/import?source=${encodeURIComponent(source)}`, { method: 'POST', headers: { 'Content-Type': 'text/csv' }, body: await file.text() });
      setNote(`${d.found} address${d.found === 1 ? '' : 'es'} in the file: ${d.added} added, ${d.alreadyListed} already on the list${d.named ? ` (${d.named} given the name from the file)` : ''}${d.notSubscribed ? `, ${d.notSubscribed} left out because they unsubscribed or bounced` : ''}${d.skipped ? `, ${d.skipped} line${d.skipped === 1 ? '' : 's'} skipped (not an email address)` : ''}.`);
      await load();
    } catch (e) { setError(e.message); }
    setBusy('');
    if (fileRef.current) fileRef.current.value = '';
  };
  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.email} from the list?`)) return;
    try { await api(`/admin/signups/emails/${r.id}`, { method: 'DELETE' }); await load(); } catch (e) { setError(e.message); }
  };

  const card = { ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` };
  const tile = isDark ? '#1e2a40' : '#f4f4ef';
  const input = { background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '5px 8px', color: T.text, fontSize: 12.5, fontFamily: 'inherit', outline: 'none' };
  const emails = (data?.emails || []).filter((r) => !q || `${r.email} ${r.name}`.toLowerCase().includes(q.toLowerCase()));
  const texts = data?.texts || [];

  return (
    <>
      <div style={card}>
        <div style={{ ...S.settingsCardTitle, color: T.text }}>Email sign-ups{data ? ` (${data.emails.length})` : ''}</div>
        <div style={{ ...S.settingsCardDesc, color: T.textSub, marginBottom: 12 }}>
          People who signed up with the "Stay up to date" box on your website, and any list you bring in. Download the list as a
          spreadsheet to send a newsletter from any email service. You get an email each time someone new signs up.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button style={btn(true)} disabled={!!busy || !data?.emails.length} onClick={download}>{busy === 'download' ? 'Preparing…' : 'Download list (CSV)'}</button>
          <span style={{ ...sub, marginLeft: 8 }}>Bring in an old list from</span>
          <select value={source} onChange={(e) => setSource(e.target.value)} style={input}>
            <option value="mailchimp">Mailchimp</option><option value="workstand">Workstand</option><option value="import">Somewhere else</option>
          </select>
          <button style={btn(false)} disabled={!!busy} onClick={() => fileRef.current && fileRef.current.click()}>{busy === 'import' ? 'Importing…' : 'Choose a CSV file…'}</button>
          <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" style={{ display: 'none' }} onChange={(e) => importFile(e.target.files && e.target.files[0])} />
        </div>
        <div style={{ ...sub, marginTop: 6 }}>Use the subscribed list (from a Mailchimp export, the file of subscribed members). It needs a column called Email. A list of people who unsubscribed or bounced is refused, so nobody who asked to be taken off is put back. Addresses already on the list keep their date; a missing name is filled in from the file.</div>
        {note && <div style={{ fontSize: 12.5, color: '#2ecc71', marginTop: 8 }}>{note}</div>}
        {error && <div style={{ fontSize: 12.5, color: '#e74c3c', marginTop: 8 }}>{error}</div>}

        {data && data.emails.length > 0 && (
          <>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an address" style={{ ...input, marginTop: 14, width: 220 }} />
            <div style={{ display: 'grid', gap: 4, marginTop: 8, maxHeight: 420, overflow: 'auto' }}>
              {emails.slice(0, 500).map((r) => (
                <div key={r.id} style={{ background: tile, borderRadius: 6, padding: '6px 10px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 220px', minWidth: 0, fontSize: 13, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.email}{r.name ? <span style={{ color: T.textSub }}> · {r.name}</span> : null}</div>
                  <span style={{ ...sub, fontSize: 11.5 }}>{SOURCE[r.source] || r.source} · {day(r.createdAt)}</span>
                  <button style={{ ...btn(false), padding: '2px 8px', color: '#e74c3c' }} onClick={() => remove(r)}>Remove</button>
                </div>
              ))}
              {emails.length > 500 && <div style={sub}>Showing the newest 500 of {emails.length}. The download has them all.</div>}
              {!emails.length && <div style={sub}>No address matches that.</div>}
            </div>
          </>
        )}
        {data && !data.emails.length && <div style={{ ...sub, marginTop: 12 }}>No email sign-ups yet.</div>}
      </div>

      <div style={card}>
        <div style={{ ...S.settingsCardTitle, color: T.text }}>Text updates sign-ups{data ? ` (${texts.length})` : ''}</div>
        <div style={{ ...S.settingsCardDesc, color: T.textSub, marginBottom: 10 }}>
          From the Text Updates form on your website. Each sign-up keeps the exact consent wording the person agreed to.
        </div>
        <div style={{ display: 'grid', gap: 4, maxHeight: 320, overflow: 'auto' }}>
          {texts.map((r) => (
            <div key={r.id} style={{ background: tile, borderRadius: 6, padding: '6px 10px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px', fontSize: 13, color: T.text }}>{r.name}{r.phone ? <span style={{ color: T.textSub }}> · {r.phone}</span> : null}</div>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: r.consent ? '#2ecc71' : '#e67e22' }}>{r.consent ? 'Agreed to texts' : 'Did not agree'}</span>
              <span style={{ ...sub, fontSize: 11.5 }}>{day(r.createdAt)}</span>
            </div>
          ))}
          {data && !texts.length && <div style={sub}>No text sign-ups yet.</div>}
        </div>
      </div>
    </>
  );
}
